import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Edit2, X, User, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import type { ProfilePreviewResponse } from '../services/api';
import { redirectTarget } from '../utils/redirect';

function TermsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-lg max-h-[80vh] overflow-y-auto glass-card rounded-3xl p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/40 hover:text-white/70 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-lg font-semibold text-white mb-4">
              Termos de Importação de Dados
            </h2>

            <div className="space-y-4 text-sm text-white/70 leading-relaxed">
              <section>
                <h3 className="text-white font-medium mb-1">1. Consentimento para Coleta de Dados</h3>
                <p>
                  Ao fornecer seu nome de usuário e prosseguir, você autoriza expressamente o BT Vision
                  a acessar seu perfil público na plataforma de torneios e importar seu nome de
                  exibição e sua foto de perfil.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">2. Responsabilidade sobre o Nome de Usuário</h3>
                <p>
                  Você é o único responsável por fornecer o nome de usuário correto. O BT Vision não se
                  responsabiliza por dados importados de um perfil que não pertence a você em decorrência
                  de nome de usuário incorreto, digitado com erro ou pertencente a terceiro.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">3. Natureza dos Dados</h3>
                <p>
                  Os dados importados são provenientes de informações publicamente disponíveis na
                  plataforma de torneios. O BT Vision não acessa dados privados, senhas ou informações
                  de pagamento da plataforma de origem.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">4. Uso dos Dados</h3>
                <p>
                  Os dados importados serão utilizados exclusivamente para gerar estatísticas,
                  análises de desempenho e funcionalidades do BT Vision. Não compartilhamos seus
                  dados individuais com terceiros sem seu consentimento.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">5. Precisão das Informações</h3>
                <p>
                  O BT Vision apresenta os dados conforme disponibilizados pela plataforma de torneios.
                  Não garantimos a exatidão, integridade ou atualização dos dados importados, que
                  dependem da plataforma de origem.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">6. Limitação de Responsabilidade</h3>
                <p>
                  O BT Vision não possui vínculo com a plataforma de torneios e não se responsabiliza
                  por indisponibilidade, alterações ou remoção de dados na plataforma de origem.
                  Eventuais inconsistências nos dados importados não geram direito a indenização.
                </p>
              </section>

              <section>
                <h3 className="text-white font-medium mb-1">7. Remoção de Dados</h3>
                <p>
                  Você pode solicitar a exclusão dos seus dados importados a qualquer momento
                  através das configurações da sua conta ou entrando em contato conosco.
                </p>
              </section>
            </div>

            <div className="mt-6">
              <Button onClick={onClose} fullWidth>
                <span>Entendi</span>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ConnectLetzplayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriber, refreshSubscriber, signOut } = useAuth();

  // If we already have a username suggestion from the backend
  const suggestedUsername = subscriber?.letzplayUsername || '';

  const [username, setUsername] = useState(suggestedUsername);
  const [isEditing, setIsEditing] = useState(!suggestedUsername);
  const [status, setStatus] = useState<'idle' | 'previewing' | 'confirming' | 'importing' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [previewData, setPreviewData] = useState<ProfilePreviewResponse | null>(null);

  const handlePreview = async (usernameToPreview: string) => {
    if (!usernameToPreview.trim()) {
      setErrorMessage('Por favor, insira seu nome de usuário.');
      setStatus('error');
      return;
    }

    setStatus('previewing');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('sb-access-token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/vision/preview-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ letzplayUsername: usernameToPreview.trim() }),
        }
      );

      if (response.status === 401) {
        await signOut();
        window.location.href = '/login';
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Erro ao buscar perfil');
      }

      setPreviewData(data);
      setStatus('confirming');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Erro ao buscar perfil'
      );
      setStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handlePreview(username);
  };

  const handleConfirmSuggested = async () => {
    setUsername(suggestedUsername);
    await handlePreview(suggestedUsername);
  };

  const redirectTo = redirectTarget(searchParams);

  const handleSkip = () => {
    // Onboarding hands off to this page. Someone who backs out still has no
    // name, and every protected route would bounce them straight back — send
    // them to the manual form instead, carrying the destination so an invite
    // link still resolves at the end of the detour.
    if (!subscriber?.name?.trim()) {
      navigate(`/onboarding?manual=1&redirect=${encodeURIComponent(redirectTo)}`, {
        replace: true,
      });
      return;
    }
    navigate(redirectTo, { replace: true });
  };

  const handleConfirmConnect = async () => {
    setStatus('importing');

    try {
      const token = localStorage.getItem('sb-access-token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/vision/connect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ letzplayUsername: username.trim() }),
        }
      );

      if (response.status === 401) {
        await signOut();
        window.location.href = '/login';
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Erro ao conectar conta');
      }

      await refreshSubscriber();
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Erro ao conectar conta'
      );
      setStatus('error');
      setPreviewData(null);
    }
  };

  const handleReject = () => {
    setPreviewData(null);
    setUsername('');
    setIsEditing(true);
    setStatus('idle');
    setErrorMessage('');
  };

  // Importing is a single profile fetch — a brief spinner, not a full skeleton
  if (status === 'importing') {
    return (
      <PageContainer className="flex flex-col items-center justify-center min-h-screen gap-4 px-4">
        <LoadingSpinner size="lg" />
        <p className="text-white/60 text-sm">Importando seu perfil...</p>
      </PageContainer>
    );
  }

  // Show profile confirmation card
  if (status === 'confirming' && previewData) {
    const { profile, recentActivity } = previewData;

    return (
      <PageContainer className="flex items-center justify-center min-h-screen px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Logo size="lg" className="mb-2" />
          </div>

          <div className="glass-card rounded-3xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">
                Este é seu perfil?
              </h2>
            </div>

            {/* Profile card */}
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
              {/* Avatar + name */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-white/10 flex-shrink-0 flex items-center justify-center">
                  {profile.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-white/40" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-white">{profile.name}</p>
                  <p className="text-sm text-white/50">@{profile.username}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-4 text-sm text-white/60 mb-4">
                <span>{profile.totalGames} partidas</span>
                <span className="text-white/20">|</span>
                <span>{profile.totalTournaments} torneios</span>
              </div>

              {/* Recent activity */}
              {recentActivity && recentActivity.lastMatchDate && (
                <div className="border-t border-white/10 pt-3 space-y-1.5 text-sm">
                  <p className="text-white/50">
                    Última partida:{' '}
                    <span className="text-white/80">{formatDate(recentActivity.lastMatchDate)}</span>
                  </p>
                  {recentActivity.lastTournamentName && (
                    <p className="text-white/50">
                      Torneio:{' '}
                      <span className="text-white/80">{recentActivity.lastTournamentName}</span>
                    </p>
                  )}
                  {recentActivity.lastPartner && (
                    <p className="text-white/50">
                      Parceiro(a):{' '}
                      <span className="text-white/80">{recentActivity.lastPartner}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="space-y-3">
              <Button onClick={handleConfirmConnect} fullWidth>
                <Check className="w-5 h-5" />
                <span>Sim, este sou eu!</span>
              </Button>

              <button
                onClick={handleReject}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-white/60 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Não é meu perfil</span>
              </button>

              <button
                onClick={handleSkip}
                className="w-full text-center py-1 text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                Pular
              </button>
            </div>

            {/* Terms link */}
            <p className="mt-4 text-center text-xs text-white/40">
              Ao continuar, você aceita os{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-turquoise hover:text-turquoise/80 underline transition-colors"
              >
                termos de importação de dados
              </button>
              .
            </p>
          </div>
        </motion.div>

        <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      </PageContainer>
    );
  }

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="lg" className="mb-2" />
        </div>

        {/* Card */}
        <div className="glass-card rounded-3xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">
              Importar seu perfil da plataforma de torneios
            </h2>
            <p className="text-white/60 text-sm">
              Opcional — usamos apenas seu nome e foto para te identificar nos
              rankings. Você pode pular e preencher isso depois.
            </p>
          </div>

          {/* Instruction image */}
          <div className="mb-6">
            <img
              src="/username_instruction.jpeg"
              alt="Como encontrar seu nome de usuário na plataforma de torneios"
              className="w-full rounded-xl border border-white/10"
            />
          </div>

          {/* If we have a suggestion */}
          {suggestedUsername && !isEditing ? (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 border border-turquoise/30">
                <p className="text-sm text-white/60 mb-1">Username encontrado:</p>
                <p className="text-lg font-semibold text-turquoise">{suggestedUsername}</p>
              </div>

              <Button onClick={handleConfirmSuggested} fullWidth isLoading={status === 'previewing'}>
                <Search className="w-5 h-5" />
                <span>Buscar perfil</span>
              </Button>

              <button
                onClick={() => setIsEditing(true)}
                className="w-full flex items-center justify-center gap-2 py-2 text-white/60 hover:text-white transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Não é você? Alterar username</span>
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.replace(/[\s@]/g, ''));
                  if (status === 'error') setStatus('idle');
                }}
                placeholder="seu-username"
                error={status === 'error' ? errorMessage : undefined}
                disabled={status === 'previewing'}
              />
              {status !== 'error' && (
                <p className="mt-1 text-xs text-white/50">Sem o "@" — apenas o nome de usuário</p>
              )}

              <Button type="submit" fullWidth isLoading={status === 'previewing'}>
                <Search className="w-5 h-5" />
                <span>Buscar perfil</span>
              </Button>

              {suggestedUsername && (
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="w-full text-center py-2 text-white/60 hover:text-white transition-colors"
                >
                  Voltar para sugestão
                </button>
              )}
            </form>
          )}

          <button
            type="button"
            onClick={handleSkip}
            className="w-full text-center py-3 mt-2 text-white/50 hover:text-white transition-colors"
          >
            Pular por enquanto
          </button>

          {/* Terms link */}
          <p className="mt-2 text-center text-xs text-white/40">
            Ao continuar, você aceita os{' '}
            <button
              type="button"
              onClick={() => setShowTerms(true)}
              className="text-turquoise hover:text-turquoise/80 underline transition-colors"
            >
              termos de importação de dados
            </button>
            .
          </p>
        </div>
      </motion.div>

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </PageContainer>
  );
}
